class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = null;
    this.searchTerm = '';
    this.defaultSearch = this.querySelector('.default--search');
    this.demoSearch = this.querySelector('.wbsclose');
    this.searchActiveClass = this.dataset.searchActiveClass;
    this.preventCloseOnBlur = false;
    this.debounceTimer = null;
    this.statusElement = null;
    this.loadingText = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.input?.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    if (this.input) {
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('blur', () => {
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
          this.preventCloseOnBlur = true;
          setTimeout(() => {
            this.preventCloseOnBlur = false;
          }, 500);
        }
      });
    }

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.demoSearch) {
      this.demoSearch.addEventListener('click', this.onClick.bind(this));
    }
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  getQueryKey(searchTerm) {
    return searchTerm.trim().toLowerCase().replace(/\s+/g, '-');
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    if (this.defaultSearch && !this.defaultSearch.classList.contains('hidden')) {
      this.defaultSearch.classList.add('hidden');
    }

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      if (this.defaultSearch) this.defaultSearch.classList.remove('hidden');
      this.close(true);
      return;
    }

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.getSearchResults(this.searchTerm);
    }, 250);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset() {
    super.onFormReset();

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      clearTimeout(this.debounceTimer);

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.defaultSearch) this.defaultSearch.classList.remove('hidden');
      if (this.demoSearch) this.demoSearch.classList.remove('hidden');
      this.hiddenClassActivity(true);
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.hiddenClassActivity(true);
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (window.innerWidth <= 991) return;
      if (this.preventCloseOnBlur) return;

      if (!this.contains(document.activeElement)) {
        if (this.defaultSearch) this.defaultSearch.classList.add('hidden');
        if (this.demoSearch) this.demoSearch.classList.add('hidden');
        this.hiddenClassActivity(false);
        this.close();
      }
    }, 100);
  }

  onClick() {
    setTimeout(() => {
      if (this.defaultSearch) this.defaultSearch.classList.add('hidden');
      if (this.demoSearch) this.demoSearch.classList.add('hidden');
      this.hiddenClassActivity(false);
      this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown' || event.code === 'Enter') {
      this.preventCloseOnBlur = true;

      setTimeout(() => {
        this.preventCloseOnBlur = false;
      }, 500);

      event.preventDefault();
    }
  }

  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (!currentButtonText) return;

    if (!previousTerm) {
      searchForTextElement.innerText = currentButtonText.replace(/["“”]?$/, `${newTerm}`);
      return;
    }

    const escapedPreviousTerm = this.escapeRegExp(previousTerm);
    const matches = currentButtonText.match(new RegExp(escapedPreviousTerm, 'g'));

    if (matches && matches.length > 1) {
      return;
    }

    const newButtonText = currentButtonText.replace(new RegExp(escapedPreviousTerm), newTerm);
    searchForTextElement.innerText = newButtonText;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);

    if (!allVisibleElements.length) return;
    if (moveUp && !selectedElement) return;

    let activeElementIndex = 0;
    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', 'true');

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', 'false');
    }

    if (this.input) {
      this.input.setAttribute('aria-activedescendant', activeElement.id || '');
    }
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = this.getQueryKey(searchTerm);
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const searchParams = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,collection,page,article,query',
      'resources[limit]': '8',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search'
    });

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          this.hiddenClassActivity(false);
          this.close();
          throw new Error(`Predictive search request failed with status ${response.status}`);
        }

        return response.text();
      })
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const predictiveSearchSection = parsed.querySelector('#shopify-section-predictive-search');

        if (!predictiveSearchSection) {
          throw new Error('Predictive search section not found in response.');
        }

        const resultsMarkup = predictiveSearchSection.innerHTML;

        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });

        if (this.searchTerm === searchTerm) {
          this.renderSearchResults(resultsMarkup);
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error.code === 20) {
          return;
        }

        this.hiddenClassActivity(false);
        this.close();
        console.error(error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    if (this.loadingText) {
      this.setLiveRegionText(this.loadingText);
    }

    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');

    this.setLiveRegionResults();
    this.hiddenClassActivity(true);
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const resultsCountElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (resultsCountElement) {
      this.setLiveRegionText(resultsCountElement.textContent);
    }
  }

  open() {
    this.setAttribute('open', 'true');

    if (this.input) {
      this.input.setAttribute('aria-expanded', 'true');
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute('aria-selected', 'false');
    }

    if (this.input) {
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
    }

    this.removeAttribute('loading');
    this.removeAttribute('open');

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  hiddenClassActivity(isAddOperation = false) {
    if (!this.searchActiveClass) return;

    if (!document.body.classList.contains(this.searchActiveClass) && isAddOperation) {
      document.body.classList.add(this.searchActiveClass);
    } else if (document.body.classList.contains(this.searchActiveClass) && !isAddOperation) {
      document.body.classList.remove(this.searchActiveClass);
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);